import {
  isPslfEligiblePlanKey,
  type SortPlansOutput,
  type SortPlansPayload,
  type SortedPlanRow,
} from 'tio-common/utils/strategy-finder-0726';
import type {
  GeneratePaymentPlanResponse,
  PaymentPlanRow,
} from 'tio-data/requests/payment-plan-explorer';

// A port of tio-user-profile's payment-plan-explorer, so demo rows are the engine's rows.

const NSP = 'newStandardPlan';
const RAP = 'repaymentAssistancePlan';
const TEN_YEARS_STANDARD = 'tenYearsStandard';
const EXTENDED_PLAN = 'extendedPlan';
const GRADUATED = 'graduated';
const IBR = 'ibr';
const ICR = 'icr';
const PAYE = 'paye';

// RepaymentPlans members the engine has no schedule for, by enum member name.
const UNIMPLEMENTED_PLAN_NAMES = new Map([
  ['noRepaymentPlan', 'NO_REPAYMENT_PLAN'],
  ['refinancing', 'REFINANCING'],
  ['repaye', 'REPAYE'],
]);

const IDR_PLANS = [RAP, PAYE, ICR, IBR];
const NON_CAP_INTEREST_PLANS = [PAYE, ICR, IBR];
const DIRECT_PLANS = [NSP, RAP, TEN_YEARS_STANDARD, EXTENDED_PLAN, GRADUATED, IBR, ICR, PAYE];
const NEVER_BEST_PLANS = [EXTENDED_PLAN, GRADUATED];

const PLAN_LABELS: Record<string, string> = {
  [NSP]: 'New Standard Plan',
  [RAP]: 'Repayment Assistance Plan',
  [TEN_YEARS_STANDARD]: '10 Year Standard',
  [EXTENDED_PLAN]: 'Extended Plan',
  [GRADUATED]: 'Graduated',
  [IBR]: 'IBR',
  [ICR]: 'ICR',
  [PAYE]: 'PAYE',
};

const INVALID_LOAN_TIMES_WARNING =
  'You did not choose a valid combination of first and last loan times. Please review your answers and try again.';
const IBR_UNAVAILABLE_ERROR = 'IBR is not available for loans first disbursed after June 30, 2026.';

// 2026 HHS poverty guidelines, as in the engine's ThresholdStart and ThresholdIncrements.
const POVERTY_THRESHOLD_CENTS = {
  AK: { start: 1995000, increment: 710000 },
  HI: { start: 1836000, increment: 653000 },
  other: { start: 1596000, increment: 568000 },
};

const IDR_SET_PRESERVING = [TEN_YEARS_STANDARD, IBR, ICR, PAYE, GRADUATED, EXTENDED_PLAN];
const PAID_MONTHS_PRESERVED = new Map<string, string[]>([
  [NSP, [NSP]],
  [RAP, [NSP, RAP]],
  [TEN_YEARS_STANDARD, IDR_SET_PRESERVING],
  [EXTENDED_PLAN, IDR_SET_PRESERVING],
  [GRADUATED, IDR_SET_PRESERVING],
  [IBR, IDR_SET_PRESERVING],
  [ICR, IDR_SET_PRESERVING],
  [PAYE, IDR_SET_PRESERVING],
]);

class EngineError extends Error {}

interface Schedule {
  payments: number[];
  balances: number[];
}

interface Profile {
  title?: string;
  error?: string;
  balances: number[];
  employeePayments: number[];
  employerPayments: number[];
  forgivenessAmounts: number[];
  subsidyAmounts: number[];
  finalBalance: number;
  employeePaid: number;
  employerPaid: number;
  forgivenessPaid: number;
  tlfForgiveness: number;
  finalBalanceForgiveness: number;
  subsidyPaid: number;
}

interface ProfileRequest {
  interestRateAnnual: number;
  plan: string;
  currentBalance: number;
  paidMonths: number;
  originalDisbursementAmount: number;
  children: number;
  adults: number;
  agi: number;
  agiGrowthRate: number;
  firstLoanTime: string;
  loanType: string;
  tlfAmount: number;
  pslf: boolean;
  residenceState: string;
  multiplier: number;
}

export type GenerateScheduleResponse = Omit<GeneratePaymentPlanResponse, 'warning'> & {
  warning: string | null;
};

export interface GenerateErrorResponse {
  error: string;
  is_error: true;
}

const EMPTY_SCHEDULE: Schedule = { payments: [], balances: [] };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const finite = (value: unknown, fallback = 0) =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

const integer = (value: unknown, fallback = 0) => Math.round(finite(value, fallback));

// Python's round(): the nearest integer, ties to even.
function pyRound(value: number) {
  const floor = Math.floor(value);
  const fraction = value - floor;
  if (fraction === 0.5) {
    return floor % 2 === 0 ? floor : floor + 1;
  }
  return fraction < 0.5 ? floor : floor + 1;
}

function pyDivide(dividend: number, divisor: number) {
  if (divisor === 0) {
    throw new EngineError('division by zero');
  }
  return dividend / divisor;
}

const interestRateToMonthly = (annualRate: number) => Math.pow(1 + annualRate / 365, 365 / 12) - 1;

function monthlyPayment(rate: number, periods: number, presentValue: number) {
  const value = Math.pow(1 + rate, periods);
  return rate !== 0 ? (rate * (presentValue * value)) / (1 - value) : -presentValue / periods;
}

function discretionaryIncome(
  state: string,
  householdSize: number,
  annualIncome: number,
  povertyMultiplier: number
) {
  const threshold =
    state === 'AK' || state === 'HI'
      ? POVERTY_THRESHOLD_CENTS[state]
      : POVERTY_THRESHOLD_CENTS.other;
  const income =
    annualIncome -
    povertyMultiplier * (threshold.start + threshold.increment * (householdSize - 1));
  if (income <= 0) {
    return 0;
  }
  return income < 100 ? 100 : income;
}

function fixedSchedule(
  currentBalance: number,
  rate: number,
  totalMonths: number,
  paidMonthsIn: number
): Schedule {
  if (currentBalance <= 0 || paidMonthsIn >= totalMonths) {
    return EMPTY_SCHEDULE;
  }
  const paidMonths = Math.max(0, paidMonthsIn);
  const paymentLevel = pyRound(monthlyPayment(rate, totalMonths, -currentBalance));
  const payments = new Array<number>(totalMonths).fill(paymentLevel);
  const balances = new Array<number>(totalMonths).fill(0);
  let adjustmentFactor = 1.0;
  if (paidMonths > 0) {
    for (let i = 0; i < paidMonths; i++) {
      const prior = i === 0 ? currentBalance : (balances[i - 1] ?? 0);
      balances[i] = pyRound(prior * (1 + rate)) - (payments[i] ?? 0);
    }
    adjustmentFactor = pyDivide(currentBalance, balances[paidMonths - 1] ?? 0);
  }
  const adjustedPaymentLevel = pyRound(paymentLevel * adjustmentFactor);
  for (let i = paidMonths; i < totalMonths; i++) {
    payments[i] = adjustedPaymentLevel;
    const prior = i === paidMonths ? currentBalance : (balances[i - 1] ?? 0);
    balances[i] = pyRound(prior * (1 + rate) - adjustedPaymentLevel);
  }
  return { payments: payments.slice(paidMonths), balances: balances.slice(paidMonths) };
}

function nspTermMonths(originalDisbursementAmount: number) {
  if (originalDisbursementAmount < 2500000) {
    return 120;
  }
  if (originalDisbursementAmount < 5000000) {
    return 180;
  }
  return originalDisbursementAmount < 10000000 ? 240 : 300;
}

function graduatedPaymentLevels(initialBalance: number, rate: number) {
  if (initialBalance <= 0) {
    return [0, 0, 0, 0, 0];
  }
  const fourthRootOfThree = Math.pow(3, 1 / 4);
  const squareRootOfThree = Math.pow(3, 1 / 2);
  const fourthRootOfThreeCubed = Math.pow(3, 3 / 4);
  const steps = [1, fourthRootOfThree, squareRootOfThree, fourthRootOfThreeCubed, 3];
  if (rate <= 0) {
    const helper =
      initialBalance / 24 / (4 + fourthRootOfThree + squareRootOfThree + fourthRootOfThreeCubed);
    return steps.map((step) => pyRound(step === 1 ? helper : step * helper));
  }
  const b = 1 / Math.pow(1 + rate, 24);
  const d =
    1 +
    fourthRootOfThree * b +
    squareRootOfThree * Math.pow(b, 2) +
    fourthRootOfThreeCubed * Math.pow(b, 3) +
    3 * Math.pow(b, 4);
  const helper = (initialBalance * rate) / ((1 - b) * d);
  return steps.map((step) => pyRound(step === 1 ? helper : step * helper));
}

function graduatedSchedule(currentBalance: number, rate: number, paidMonthsIn: number): Schedule {
  if (currentBalance <= 0 || paidMonthsIn >= 120) {
    return EMPTY_SCHEDULE;
  }
  const paidMonths = Math.max(0, paidMonthsIn);
  const levels = graduatedPaymentLevels(currentBalance, rate);
  const payments = new Array<number>(120).fill(0);
  const balances = new Array<number>(120).fill(0);
  for (let i = 0; i < 120; i++) {
    payments[i] = i % 24 === 0 ? (levels[i / 24] ?? 0) : (payments[i - 1] ?? 0);
    balances[i] =
      i === 0
        ? Math.trunc(currentBalance)
        : pyRound((balances[i - 1] ?? 0) * (1 + rate) - (payments[i - 1] ?? 0));
  }
  const adjustmentFactor = pyDivide(currentBalance, balances[paidMonths] ?? 0);
  for (let i = paidMonths; i < 120; i++) {
    balances[i] = pyRound((balances[i] ?? 0) * adjustmentFactor);
    payments[i] = pyRound((payments[i] ?? 0) * adjustmentFactor);
  }
  return { payments: payments.slice(paidMonths), balances: balances.slice(paidMonths) };
}

function rapSchedule(request: ProfileRequest, rate: number, usingTlf: boolean): Schedule {
  const { currentBalance, children, agiGrowthRate, multiplier } = request;
  const totalMonths = 360 + (usingTlf ? 60 : 0);
  if (currentBalance <= 0 || request.paidMonths >= totalMonths) {
    return EMPTY_SCHEDULE;
  }
  const householdPayment = (agi: number) => {
    const paymentFactor = Math.min(0.01 * Math.floor((agi - 1) / 1000000), 0.1);
    return pyRound(
      multiplier * Math.max(1000, pyRound((agi * paymentFactor) / 12) - children * 5000)
    );
  };
  const payments: number[] = [];
  const balances: number[] = [];
  let currentAgi = request.agi;
  let currentPayment = householdPayment(currentAgi);
  let runningBalance = currentBalance;
  for (let i = Math.max(0, request.paidMonths); i < totalMonths; i++) {
    if (runningBalance <= 0) {
      break;
    }
    if (i % 12 === 0 && i > 0) {
      currentAgi = pyRound(currentAgi * (1 + agiGrowthRate));
      currentPayment = Math.max(
        Math.min(householdPayment(currentAgi), runningBalance * (1 + rate)),
        pyRound(multiplier * 1000)
      );
    }
    payments.push(currentPayment);
    let nextBalance = pyRound(runningBalance * (1 + rate) - currentPayment);
    if (nextBalance <= 0) {
      nextBalance = 0;
    }
    if (nextBalance > Math.max(runningBalance - 5000, 0)) {
      nextBalance = Math.max(runningBalance - 5000, 0);
    }
    balances.push(nextBalance);
    runningBalance = nextBalance;
  }
  return { payments, balances };
}

interface IncomeDrivenTerms {
  baseMonths: number;
  incomeFactor: number;
  povertyMultiplier: number;
  standardMonths: number;
  minimum: (payment: number, multiplier: number) => number;
}

const INCOME_DRIVEN_TERMS = new Map<string, IncomeDrivenTerms>([
  [
    PAYE,
    {
      baseMonths: 240,
      incomeFactor: 0.1,
      povertyMultiplier: 1.5,
      standardMonths: 120,
      minimum: (payment, multiplier) => {
        if (payment < pyRound(multiplier * 500)) {
          return 0;
        }
        return payment < pyRound(multiplier * 1000) ? pyRound(multiplier * 1000) : payment;
      },
    },
  ],
  [
    ICR,
    {
      baseMonths: 300,
      incomeFactor: 0.2,
      povertyMultiplier: 1.0,
      standardMonths: 144,
      minimum: (payment, multiplier) =>
        payment > 0 && payment < pyRound(multiplier * 500) ? pyRound(multiplier * 500) : payment,
    },
  ],
]);

function ibrTerms(firstLoanTime: string): IncomeDrivenTerms | undefined {
  const oldCohort = firstLoanTime === 'old_ibr_cohort' || firstLoanTime === 'old_borrower';
  if (!oldCohort && firstLoanTime !== 'new_ibr_cohort') {
    return undefined;
  }
  return {
    baseMonths: oldCohort ? 300 : 240,
    incomeFactor: oldCohort ? 0.15 : 0.1,
    povertyMultiplier: 1.5,
    standardMonths: 120,
    minimum: (payment, multiplier) => (payment < pyRound(multiplier * 100) ? 0 : payment),
  };
}

function incomeDrivenSchedule(
  request: ProfileRequest,
  terms: IncomeDrivenTerms,
  rate: number,
  usingTlf: boolean
): Schedule {
  const { currentBalance, multiplier, agiGrowthRate } = request;
  const totalMonths = terms.baseMonths + (usingTlf ? 60 : 0);
  if (currentBalance <= 0 || request.paidMonths >= totalMonths) {
    return EMPTY_SCHEDULE;
  }
  const householdSize = request.adults + request.children;
  const incomePayment = (agi: number) =>
    pyRound(
      multiplier *
        terms.incomeFactor *
        (discretionaryIncome(request.residenceState, householdSize, agi, terms.povertyMultiplier) /
          12)
    );
  const standardPayment = pyRound(
    monthlyPayment(
      rate,
      terms.standardMonths,
      -(request.originalDisbursementAmount || currentBalance)
    )
  );
  const payments: number[] = [];
  const balances: number[] = [];
  let currentAgi = request.agi;
  let currentPayment = Math.min(incomePayment(currentAgi), standardPayment);
  let runningBalance = currentBalance;
  let nonCapitalized = 0;
  for (let i = Math.max(0, request.paidMonths); i < totalMonths; i++) {
    if (runningBalance <= 0 && nonCapitalized <= 0) {
      break;
    }
    if (i % 12 === 0 && i > 0) {
      currentAgi = pyRound(currentAgi * (1 + agiGrowthRate));
      currentPayment = terms.minimum(
        Math.min(incomePayment(currentAgi), standardPayment),
        multiplier
      );
    }
    payments.push(currentPayment);
    nonCapitalized += pyRound(runningBalance * rate);
    const toNonCapitalized = Math.min(currentPayment, nonCapitalized);
    nonCapitalized -= toNonCapitalized;
    runningBalance -= Math.min(currentPayment - toNonCapitalized, runningBalance);
    balances.push(runningBalance);
  }
  return { payments, balances };
}

function applyPaymentEvents(
  schedule: Schedule,
  request: ProfileRequest,
  rate: number,
  tlfAmount: number
): Profile {
  const { plan, currentBalance, paidMonths } = request;
  // Estimators send no payment_events, so Teacher Loan Forgiveness is the only event.
  const tlfMonth = tlfAmount > 0 && paidMonths <= 60 ? 60 - paidMonths : undefined;
  const pslf = request.pslf && IDR_PLANS.includes(plan);
  const forgiveFinalBalance = IDR_PLANS.includes(plan);
  const titleParts = [PLAN_LABELS[plan] ?? plan];
  if (tlfAmount > 0 && paidMonths < 60) {
    titleParts.push('TLF');
  }
  if (pslf) {
    titleParts.push('PSLF');
  }
  const title = titleParts.join(' + ');

  let { payments, balances } = schedule;
  if (pslf) {
    const pslfCutoff = tlfAmount > 0 ? 180 : 120;
    if (paidMonths >= pslfCutoff) {
      return {
        title,
        balances: [0],
        employeePayments: [0],
        employerPayments: [0],
        forgivenessAmounts: [currentBalance],
        subsidyAmounts: [0],
        finalBalance: 0,
        employeePaid: 0,
        employerPaid: 0,
        forgivenessPaid: currentBalance,
        tlfForgiveness: 0,
        finalBalanceForgiveness: currentBalance,
        subsidyPaid: 0,
      };
    }
    payments = payments.slice(0, pslfCutoff - paidMonths);
    balances = balances.slice(0, pslfCutoff - paidMonths);
  }

  const newBalances: number[] = [];
  const employeePayments: number[] = [];
  const forgivenessAmounts: number[] = [];
  const subsidyAmounts: number[] = [];
  let employeePaid = 0;
  let forgivenessPaid = 0;
  let subsidyPaid = 0;
  let runningBalance = currentBalance;
  let nonCapitalized = 0;

  for (let i = 0; i < payments.length; i++) {
    if (runningBalance <= 0 && nonCapitalized <= 0) {
      break;
    }
    if (NON_CAP_INTEREST_PLANS.includes(plan)) {
      nonCapitalized += pyRound(runningBalance * rate);
    } else {
      runningBalance = pyRound(runningBalance * (1 + rate));
    }
    const payment = payments[i] ?? 0;
    const scheduleBalance = balances[i] ?? 0;
    let builtInBalanceDrop = 0;
    if (plan === RAP) {
      const priorScheduleBalance = i === 0 ? currentBalance : (balances[i - 1] ?? 0);
      if (pyRound(priorScheduleBalance * (1 + rate) - scheduleBalance - payment) > 0) {
        builtInBalanceDrop = priorScheduleBalance - scheduleBalance;
      }
    }
    const scheduledPayment = Math.min(payment, runningBalance + nonCapitalized);
    let monthEmployeePayment = 0;
    let monthForgiveness = 0;
    let monthSubsidy = 0;

    const totalOwed = runningBalance + nonCapitalized;
    if (i + 1 === tlfMonth && totalOwed > 0) {
      const eventAmount = Math.min(tlfAmount, totalOwed);
      const toNonCapitalized = Math.min(eventAmount, nonCapitalized);
      nonCapitalized -= toNonCapitalized;
      const toPrincipal = Math.min(eventAmount - toNonCapitalized, runningBalance);
      runningBalance -= toPrincipal;
      forgivenessPaid += toNonCapitalized + toPrincipal;
      monthForgiveness += toNonCapitalized + toPrincipal;
    }
    runningBalance = Math.max(0, runningBalance);
    nonCapitalized = Math.max(0, nonCapitalized);

    const adjustedPayment = Math.min(scheduledPayment, runningBalance + nonCapitalized);
    employeePaid += adjustedPayment;
    monthEmployeePayment += adjustedPayment;
    const toNonCapitalized = Math.min(adjustedPayment, nonCapitalized);
    nonCapitalized -= toNonCapitalized;
    runningBalance -= Math.min(adjustedPayment - toNonCapitalized, runningBalance);

    if (builtInBalanceDrop > 0) {
      const floor = (i > 0 ? (newBalances[i - 1] ?? 0) : currentBalance) - builtInBalanceDrop;
      if (runningBalance > floor) {
        const newRunningBalance = Math.max(floor, 0);
        subsidyPaid += runningBalance - newRunningBalance;
        monthSubsidy += runningBalance - newRunningBalance;
        runningBalance = newRunningBalance;
      }
    }

    employeePayments.push(monthEmployeePayment);
    forgivenessAmounts.push(monthForgiveness);
    subsidyAmounts.push(monthSubsidy);
    newBalances.push(Math.max(0, runningBalance + nonCapitalized));
  }

  const last = newBalances.length - 1;
  let finalBalance = newBalances[last] ?? 0;
  const tlfForgiveness = forgivenessPaid;
  let finalBalanceForgiveness = 0;
  if (last >= 0) {
    if (forgiveFinalBalance) {
      finalBalanceForgiveness = finalBalance;
      forgivenessPaid += finalBalance;
      forgivenessAmounts[last] = (forgivenessAmounts[last] ?? 0) + finalBalance;
      newBalances[last] = 0;
      finalBalance = 0;
    } else if (finalBalance > 0 && finalBalance < 1000) {
      employeePaid += finalBalance;
      employeePayments[last] = (employeePayments[last] ?? 0) + finalBalance;
      newBalances[last] = 0;
      finalBalance = 0;
    }
  }

  return {
    title,
    balances: newBalances,
    employeePayments,
    employerPayments: employeePayments.map(() => 0),
    forgivenessAmounts,
    subsidyAmounts,
    finalBalance,
    employeePaid,
    employerPaid: 0,
    forgivenessPaid,
    tlfForgiveness,
    finalBalanceForgiveness,
    subsidyPaid,
  };
}

const isTlfEligible = (loanType: string) => loanType === 'direct' || loanType === 'ffel';

// Throws EngineError for a plan with no schedule, or a zero divisor in a fixed or graduated schedule.
function generatePaymentProfile(request: ProfileRequest): Profile {
  const multiplier = Math.min(Math.max(request.multiplier, 0), 1);
  const scaled = { ...request, multiplier };
  const tlfAmount = pyRound((isTlfEligible(request.loanType) ? request.tlfAmount : 0) * multiplier);
  const usingTlf = tlfAmount > 0;
  const rate = interestRateToMonthly(request.interestRateAnnual);
  const { currentBalance, paidMonths } = request;
  let schedule: Schedule;
  if (request.plan === GRADUATED) {
    schedule = graduatedSchedule(currentBalance, rate, paidMonths);
  } else if (request.plan === TEN_YEARS_STANDARD) {
    schedule = fixedSchedule(currentBalance, rate, 120, paidMonths);
  } else if (request.plan === EXTENDED_PLAN) {
    schedule = fixedSchedule(currentBalance, rate, 300, paidMonths);
  } else if (request.plan === NSP) {
    const original = request.originalDisbursementAmount || currentBalance;
    schedule = fixedSchedule(currentBalance, rate, nspTermMonths(original), paidMonths);
  } else if (request.plan === RAP) {
    schedule = rapSchedule(scaled, rate, usingTlf);
  } else if (request.plan === IBR) {
    const terms = ibrTerms(request.firstLoanTime);
    if (!terms) {
      return {
        error: IBR_UNAVAILABLE_ERROR,
        balances: [],
        employeePayments: [],
        employerPayments: [],
        forgivenessAmounts: [],
        subsidyAmounts: [],
        finalBalance: 0,
        employeePaid: 0,
        employerPaid: 0,
        forgivenessPaid: 0,
        tlfForgiveness: 0,
        finalBalanceForgiveness: 0,
        subsidyPaid: 0,
      };
    }
    schedule = incomeDrivenSchedule(scaled, terms, rate, usingTlf);
  } else {
    const terms = INCOME_DRIVEN_TERMS.get(request.plan);
    if (!terms) {
      const name = UNIMPLEMENTED_PLAN_NAMES.get(request.plan) ?? request.plan;
      throw new EngineError(`Payment plan RepaymentPlans.${name} not implemented`);
    }
    schedule = incomeDrivenSchedule(scaled, terms, rate, usingTlf);
  }
  return applyPaymentEvents(schedule, request, rate, tlfAmount);
}

function availableRepaymentPlans(firstLoanTime: string, lastLoanTime: string, loanType: string) {
  if (lastLoanTime === 'pre_obbb_cohort' && firstLoanTime === 'obbb_cohort') {
    return { plans: [], warning: INVALID_LOAN_TIMES_WARNING };
  }
  const obbbLast = lastLoanTime === 'obbb_cohort';
  if (loanType === 'plus') {
    return {
      plans: obbbLast ? [NSP] : [TEN_YEARS_STANDARD, GRADUATED, EXTENDED_PLAN],
      warning: null,
    };
  }
  if (loanType === 'ffel') {
    return {
      plans: obbbLast ? [NSP, RAP] : [TEN_YEARS_STANDARD, GRADUATED, EXTENDED_PLAN, IBR],
      warning: null,
    };
  }
  if (obbbLast) {
    return { plans: [NSP, RAP], warning: null };
  }
  if (firstLoanTime === 'old_borrower') {
    return { plans: DIRECT_PLANS.filter((plan) => plan !== PAYE), warning: null };
  }
  return { plans: DIRECT_PLANS, warning: null };
}

function effectivePaidMonths(
  currentPlan: string | null,
  plan: string,
  currentPaidMonths: number,
  pslf: boolean,
  tlfAmount: number
): [number, boolean] {
  if (pslf || tlfAmount > 0 || !currentPlan) {
    return [currentPaidMonths, true];
  }
  return (PAID_MONTHS_PRESERVED.get(currentPlan) ?? []).includes(plan)
    ? [currentPaidMonths, true]
    : [0, false];
}

function profileRequest(source: Record<string, unknown>, plan: string): ProfileRequest {
  const state = source['residence_state'];
  const firstLoanTime = source['first_loan_time'];
  const loanType = source['loan_type'];
  return {
    interestRateAnnual: finite(source['interest_rate_annual']),
    plan,
    currentBalance: integer(source['current_balance']),
    paidMonths: 0,
    originalDisbursementAmount: integer(source['original_disbursement_amount']),
    children: integer(source['number_of_children']),
    adults: integer(source['number_of_adults'], 1),
    agi: integer(source['agi']),
    agiGrowthRate: finite(source['agi_growth_rate']),
    firstLoanTime: typeof firstLoanTime === 'string' ? firstLoanTime : 'new_ibr_cohort',
    loanType: typeof loanType === 'string' ? loanType : 'direct',
    tlfAmount: 0,
    pslf: false,
    residenceState: typeof state === 'string' && state ? state : 'CA',
    multiplier: finite(source['multiplier'], 1),
  };
}

function lastPaymentExcluded(payments: number[]) {
  return payments.length > 1 ? payments.slice(0, -1) : payments;
}

function summarisePlan(
  base: ProfileRequest,
  paidMonths: number,
  preserved: boolean,
  tlfAmount: number,
  pslf: boolean
): SortedPlanRow {
  const result = generatePaymentProfile({ ...base, paidMonths, tlfAmount, pslf });
  const payments = result.employeePayments;
  const firstMonthPayment = payments[0] ?? 0;
  const forMax = lastPaymentExcluded(payments);
  return {
    plan: base.plan,
    title: result.title ?? base.plan,
    tlf_amount: tlfAmount,
    first_month_payment: firstMonthPayment,
    max_monthly_payment: forMax.length ? Math.max(...forMax) : firstMonthPayment,
    total_payment: result.employeePaid + result.employerPaid,
    employee_paid: result.employeePaid,
    employer_paid: result.employerPaid,
    forgiveness_paid: result.forgivenessPaid,
    tlf_forgiveness: result.tlfForgiveness,
    pslf_forgiveness: pslf ? result.finalBalanceForgiveness : 0,
    subsidy_paid: result.subsidyPaid,
    num_payments: payments.length,
    paid_months_used: paidMonths,
    paid_months_preserved: preserved,
    pslf,
    warning: result.error ?? null,
  };
}

function sortPaymentPlans(rows: SortedPlanRow[], sortBy: string) {
  const demotion = (row: SortedPlanRow) => (NEVER_BEST_PLANS.includes(row.plan) ? 1 : 0);
  const byTotal = sortBy === 'total_then_first_month';
  return [...rows].sort((a, b) => {
    const primary = byTotal
      ? a.total_payment - b.total_payment
      : a.first_month_payment - b.first_month_payment;
    const secondary = byTotal
      ? a.first_month_payment - b.first_month_payment
      : a.total_payment - b.total_payment;
    return demotion(a) - demotion(b) || primary || secondary;
  });
}

// tio-api SortPlansEstimatorFilter, which trims Direct/FFEL rows per requesting app before saving.
function filterForApp(rows: SortedPlanRow[], input: SortPlansPayload, requestingApp: string) {
  if (input.loan_type !== 'direct' && input.loan_type !== 'ffel') {
    return rows;
  }
  const expectedPslf = input.current_pslf === true && isPslfEligiblePlanKey(input.current_plan);
  const isCurrent = (row: SortedPlanRow) =>
    !!input.current_plan &&
    row.plan === input.current_plan &&
    row.tlf_amount === (input.current_tlf_amount ?? 0) &&
    row.pslf === expectedPslf;
  if (requestingApp === 'IDR-0726') {
    return rows.filter((row) => IDR_PLANS.includes(row.plan) || isCurrent(row));
  }
  if (requestingApp === 'PSLF-0726') {
    return rows.filter((row) => row.pslf || isCurrent(row));
  }
  return rows;
}

export function buildSortPlansOutput(
  input: SortPlansPayload,
  requestingApp: string
): SortPlansOutput {
  const base = profileRequest(input, '');
  const currentPlan = typeof input.current_plan === 'string' ? input.current_plan : null;
  const currentPaidMonths = integer(input.current_paid_months);
  const lastLoanTime =
    typeof input.last_loan_time === 'string' ? input.last_loan_time : 'pre_obbb_cohort';
  const available = availableRepaymentPlans(base.firstLoanTime, lastLoanTime, base.loanType);
  const requestedTlf = Array.isArray(input.tlf_amounts)
    ? input.tlf_amounts.map((amount) => integer(amount))
    : [0];
  const tlfAmounts = isTlfEligible(base.loanType) ? requestedTlf : [0];
  const pslfOptions = input.pslf_eligible === true ? [false, true] : [input.pslf === true];
  const sortBy =
    input.sort_by === 'total_then_first_month'
      ? 'total_then_first_month'
      : 'first_month_then_total';

  const rows: SortedPlanRow[] = [];
  for (const plan of available.plans) {
    for (const tlfAmount of tlfAmounts) {
      const seen = new Set<boolean>();
      for (const usePslf of pslfOptions) {
        const pslf = usePslf && IDR_PLANS.includes(plan);
        if (seen.has(pslf)) {
          continue;
        }
        seen.add(pslf);
        const [paidMonths, preserved] = effectivePaidMonths(
          currentPlan,
          plan,
          currentPaidMonths,
          pslf,
          tlfAmount
        );
        try {
          rows.push(summarisePlan({ ...base, plan }, paidMonths, preserved, tlfAmount, pslf));
        } catch (error) {
          if (!(error instanceof EngineError)) {
            throw error;
          }
        }
      }
    }
  }
  return {
    plans: filterForApp(sortPaymentPlans(rows, sortBy), input, requestingApp),
    sort_by: sortBy,
    available_plans_warning: available.warning,
    loan_type: input.loan_type,
  };
}

// Assumes the app builder's well-formed body; FastAPI's request validation is not ported.
export function simulateSchedule(body: unknown): GenerateScheduleResponse | GenerateErrorResponse {
  const source = isRecord(body) ? body : {};
  const plan = source['payment_plan'];
  const request = {
    ...profileRequest(source, typeof plan === 'string' ? plan : ''),
    paidMonths: integer(source['paid_months']),
    tlfAmount: integer(source['tlf_amount']),
    pslf: source['pslf'] === true,
  };
  let result: Profile;
  try {
    result = generatePaymentProfile(request);
  } catch (error) {
    if (error instanceof EngineError) {
      return { error: error.message, is_error: true };
    }
    throw error;
  }
  const count = Math.min(
    result.balances.length,
    result.employeePayments.length,
    result.subsidyAmounts.length,
    result.forgivenessAmounts.length
  );
  let cumulative = 0;
  const rows: PaymentPlanRow[] = [];
  for (let i = 0; i < count; i++) {
    const payment = result.employeePayments[i] ?? 0;
    cumulative += payment;
    rows.push({
      month: request.paidMonths + i + 1,
      payment,
      cumulative_payment: cumulative,
      employer_payment: result.employerPayments[i] ?? 0,
      subsidy_amount: result.subsidyAmounts[i] ?? 0,
      forgiveness_amount: result.forgivenessAmounts[i] ?? 0,
      balance: result.balances[i] ?? 0,
    });
  }
  const firstPayment = result.employeePayments[0] ?? 0;
  const forMax = lastPaymentExcluded(result.employeePayments);
  const maxPayment = forMax.length ? Math.max(...forMax) : 0;
  return {
    rows,
    title: result.title ?? '',
    employee_paid: result.employeePaid,
    employer_paid: result.employerPaid,
    forgiveness_paid: result.forgivenessPaid,
    subsidy_paid: result.subsidyPaid,
    final_balance: result.finalBalance,
    warning: result.error ?? null,
    ...(firstPayment === maxPayment
      ? { monthly_payment: firstPayment }
      : { monthly_payment_low: firstPayment, monthly_payment_high: maxPayment }),
  };
}
